import { useState, useEffect, useMemo } from 'react'
import { Loader2, Search, Filter, Copy, Trash2, X } from 'lucide-react'
import { api } from '../api/client'
import { useEntities } from '../context/EntitiesContext'
import { useMultiSelect } from '../hooks/useMultiSelect'
import { ChatHistoryComponent, DecisionAnswersComponent, JsonCell, ObjCell } from './DataDisplayComponents'
import type { IAPIExecution, IAPIDataset, TExecutionOutput } from 'extra/types/ApiModels'

function ExecutionOutput ({ output, expand }: { output: TExecutionOutput | null, expand?: boolean }) {
  if (Array.isArray(output)) {
    return <DecisionAnswersComponent answers={output} expand={expand}/>
  }
  return <ChatHistoryComponent history={output ? [output] : null} expand={expand}/>
}

interface ExecutionsListProps {
  deploymentId?: string
  promptId?: string
}

export function ExecutionsList ({ deploymentId, promptId }: ExecutionsListProps) {
  const [executions, setExecutions] = useState<Array<IAPIExecution>>([])
  const [loading, setLoading] = useState(false)
  const [cursor, setCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)

  // Filters
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<string>(deploymentId || '')
  const [searchFilters, setSearchFilters] = useState({ output: '', variables: '', chatHistory: '', metadata: '' })
  const [searchInputs, setSearchInputs] = useState({ output: '', variables: '', chatHistory: '', metadata: '' })

  const [deployments, setDeployments] = useState<Array<{ id: string, label: string | null, promptId: string }>>([])

  // Selection & Copy
  const { selectedIds, setSelectedIds, toggleSelect, toggleSelectAll } = useMultiSelect()
  const [targetDatasetId, setTargetDatasetId] = useState<string>('')
  const [copying, setCopying] = useState(false)
  const [copyMessage, setCopyMessage] = useState<string | null>(null)

  // Use global entities for datasets and prompts
  const { datasets, prompts, folders, deployments: allDeployments } = useEntities()

  // Detail modal
  const [detailExec, setDetailExec] = useState<IAPIExecution | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  useEffect(() => {
    if (!detailExec && !detailLoading) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDetailExec(null); setDetailLoading(false)
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [detailExec, detailLoading])

  const openDetail = async (id: string) => {
    setDetailLoading(true)
    setDetailExec(null)
    try {
      const exec = await api.executions.get(id)
      setDetailExec(exec)
    } catch (e) {
      console.error(e)
    } finally {
      setDetailLoading(false)
    }
  }

  // If prop changes, update filter
  useEffect(() => {
    if (deploymentId) setSelectedDeploymentId(deploymentId)
  }, [deploymentId])

  // Fetch deployments
  useEffect(() => {
    if (!deploymentId) {
      api.executions.listDeployments().then(setDeployments).catch(console.error)
    }
  }, [deploymentId])

  // Derive current prompt ID from prop or selected deployment
  const currentPromptId = useMemo(() => {
    if (promptId) return promptId
    if (selectedDeploymentId) {
      const dep = deployments.find(d => d.id === selectedDeploymentId)
      if (dep) return dep.promptId
    }
    return null
  }, [promptId, selectedDeploymentId, deployments])

  // Split datasets into current prompt and other prompts
  const { currentPromptDatasets, otherPromptsDatasets } = useMemo(() => {
    const current: Array<IAPIDataset> = []
    const other: Array<IAPIDataset> = []
    for (const d of datasets) {
      if (currentPromptId && d.promptId === currentPromptId) {
        current.push(d)
      } else {
        other.push(d)
      }
    }
    return { currentPromptDatasets: current, otherPromptsDatasets: other }
  }, [datasets, currentPromptId])

  const promptNameMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const p of prompts) {
      map.set(p.id, p.name)
    }
    return map
  }, [prompts])

  const isFilteringByDeployment = !!deploymentId || !!selectedDeploymentId

  // datasets and scenarios don't support decision prompts, so copying their executions is hidden
  const selectionHasDecisions = executions.some(e => e.isDecision && selectedIds.includes(e.id))

  const deploymentBreadcrumbMap = useMemo(() => {
    const promptMap = new Map(prompts.map(p => [p.id, p]))
    const folderMap = new Map(folders.map(f => [f.id, f]))
    const map = new Map<string, string>()
    for (const dep of allDeployments) {
      const prompt = promptMap.get(dep.promptId)
      const folder = prompt?.folderId ? folderMap.get(prompt.folderId) : null
      const parts: Array<string> = []
      if (folder) parts.push(folder.name)
      if (prompt) parts.push(prompt.name)
      parts.push(dep.label || dep.id.slice(0, 8))
      map.set(dep.id, parts.join(' > '))
    }
    return map
  }, [prompts, folders, allDeployments])

  const loadExecutions = async (reset = false) => {
    if (loading && !reset) return
    setLoading(true)
    try {
      const currentCursor = reset ? undefined : cursor
      const res = await api.executions.list(
        {
          deploymentId: selectedDeploymentId || undefined,
          searchOutput: searchFilters.output || undefined,
          searchVariables: searchFilters.variables || undefined,
          searchChatHistory: searchFilters.chatHistory || undefined,
          searchMetadata: searchFilters.metadata || undefined,
        },
        currentCursor || undefined,
      )

      setExecutions(prev => reset ? res.rows : [...prev, ...res.rows])
      setCursor(res.nextCursor)
      setHasMore(!!res.nextCursor)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadExecutions(true)
  }, [selectedDeploymentId, searchFilters])

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSearchFilters({ ...searchInputs })
  }

  const handleCopyToDataset = async () => {
    if (!targetDatasetId || selectedIds.length === 0) return
    setCopying(true)
    setCopyMessage(null)
    try {
      const res = await api.datasets.copyExecutions(targetDatasetId, selectedIds)
      setCopyMessage(`Successfully copied ${res.copied} executions to dataset.`)
      setSelectedIds([]) // Clear selection
      setTimeout(() => setCopyMessage(null), 3000)
    } catch (e) {
      console.error(e)
      setCopyMessage('Failed to copy executions.')
    } finally {
      setCopying(false)
    }
  }

  const handleCopyToScenarios = async () => {
    if (selectedIds.length === 0) return
    setCopying(true)
    setCopyMessage(null)
    try {
      const res = await api.scenarios.copyFromExecutions(selectedIds)
      setCopyMessage(`Successfully copied ${res.copied} executions to scenarios.`)
      setSelectedIds([])
      setTimeout(() => setCopyMessage(null), 3000)
    } catch (e) {
      console.error(e)
      setCopyMessage('Failed to copy executions.')
    } finally {
      setCopying(false)
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} executions?`)) return

    try {
      await api.executions.delete(selectedIds)
      setExecutions(prev => prev.filter(e => !selectedIds.includes(e.id)))
      setSelectedIds([])
    } catch (e) {
      console.error(e)
      alert('Failed to delete executions')
    }
  }

  return (
    <div className="flex flex-col bg-gray-900 text-gray-200 p-3 gap-3">
      {/* Filters Bar */}
      <div className="flex flex-wrap gap-2 items-center bg-gray-800 p-2 rounded-lg border border-gray-700">
        {!deploymentId && (
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400"/>
            <select
              value={selectedDeploymentId}
              onChange={e => setSelectedDeploymentId(e.target.value)}
              className="input w-auto text-sm"
            >
              <option value="">All Deployments</option>
              {deployments.map(d => (
                <option key={d.id} value={d.id}>
                  {d.label || d.id.slice(0, 8)} ({d.promptId ? `${d.promptId.slice(0, 8)}...` : 'no prompt'})
                </option>
              ))}
            </select>
          </div>
        )}

        <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 flex-1">
          {loading && Object.values(searchFilters).some(Boolean)
            ? <Loader2 className="w-4 h-4 text-gray-400 animate-spin"/>
            : <Search className="w-4 h-4 text-gray-400"/>}
          <input
            type="text"
            placeholder="Output"
            value={searchInputs.output}
            onChange={e => setSearchInputs(prev => ({ ...prev, output: e.target.value }))}
            className="input flex-1 text-sm"
          />
          <input
            type="text"
            placeholder="Variables"
            value={searchInputs.variables}
            onChange={e => setSearchInputs(prev => ({ ...prev, variables: e.target.value }))}
            className="input flex-1 text-sm"
          />
          <input
            type="text"
            placeholder="Chat History"
            value={searchInputs.chatHistory}
            onChange={e => setSearchInputs(prev => ({ ...prev, chatHistory: e.target.value }))}
            className="input flex-1 text-sm"
          />
          <input
            type="text"
            placeholder="Metadata"
            value={searchInputs.metadata}
            onChange={e => setSearchInputs(prev => ({ ...prev, metadata: e.target.value }))}
            className="input flex-1 text-sm"
          />
          <button type="submit" className="btn btn-primary text-sm">Search</button>
        </form>
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center bg-blue-900/20 p-2 rounded-lg border border-blue-700/50">
          <span className="text-sm font-semibold text-blue-200">{selectedIds.length} selected</span>
          <div className="h-4 w-px bg-gray-600 mx-2"/>
          {!selectionHasDecisions && (
          <>
          <select
            value={targetDatasetId}
            onChange={e => setTargetDatasetId(e.target.value)}
            className="input w-auto text-sm py-1"
          >
            <option value="">Select Dataset to Copy...</option>
            {currentPromptId && (
              <optgroup label={`Current Prompt (${promptNameMap.get(currentPromptId) || currentPromptId.slice(0, 8)})`}>
                {currentPromptDatasets.length > 0
                  ? currentPromptDatasets.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))
                  : <option disabled>No datasets</option>}
              </optgroup>
            )}
            {otherPromptsDatasets.length > 0 && (
              <optgroup label="Other Prompts">
                {otherPromptsDatasets.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({promptNameMap.get(d.promptId) || d.promptId.slice(0, 8)})
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <button
            onClick={handleCopyToDataset}
            disabled={!targetDatasetId || copying}
            className="btn btn-secondary text-sm flex items-center gap-2"
          >
            {copying ? <Loader2 className="w-4 h-4 animate-spin"/> : <Copy className="w-4 h-4"/>}
            Copy to Dataset
          </button>

          <div className="h-4 w-px bg-gray-600 mx-2"/>
          <button
            onClick={handleCopyToScenarios}
            disabled={copying}
            className="btn btn-secondary text-sm flex items-center gap-2"
          >
            {copying ? <Loader2 className="w-4 h-4 animate-spin"/> : <Copy className="w-4 h-4"/>}
            Copy as Scenario
          </button>

          <div className="h-4 w-px bg-gray-600 mx-2"/>
          </>
          )}
          <button
            onClick={handleDeleteSelected}
            className="btn btn-danger text-sm flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4"/>
            Delete
          </button>

          {copyMessage && <span className="text-sm text-green-400 ml-2">{copyMessage}</span>}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border border-gray-700 rounded-lg">
        <table className="w-full text-left text-sm border-collapse">
          <thead className="bg-gray-800 sticky top-0 z-10">
          <tr>
            <th className="p-2 border-b border-gray-700 w-8 text-center">
              <input type="checkbox" checked={executions.length > 0 && selectedIds.length === executions.length}
                     onChange={() => toggleSelectAll(executions)}/>
            </th>
            <th className="p-2 border-b border-gray-700 font-medium text-gray-400">ID / Date</th>
            <th className="p-2 border-b border-gray-700 font-medium text-gray-400">Variables</th>
            <th className="p-2 border-b border-gray-700 font-medium text-gray-400">History</th>
            <th className="p-2 border-b border-gray-700 font-medium text-gray-400">Output</th>
            <th className="p-2 border-b border-gray-700 font-medium text-gray-400">Metadata</th>
          </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
          {executions.map(exec => (
            <tr key={exec.id}
                className={`hover:bg-gray-800/50 transition-colors ${selectedIds.includes(exec.id) ? 'bg-blue-900/30' : ''}`}>
              <td className="p-2 align-top text-center">
                <input type="checkbox" checked={selectedIds.includes(exec.id)} onChange={() => toggleSelect(exec.id)}/>
              </td>
              <td className="p-2 align-top min-w-[150px]">
                <button
                  onClick={() => openDetail(exec.id)}
                  className="font-mono text-xs text-blue-400 mb-1 hover:underline cursor-pointer bg-transparent border-none p-0"
                >{exec.id}</button>
                {!isFilteringByDeployment && deploymentBreadcrumbMap.has(exec.deploymentId) && (
                  <div className="text-gray-400 text-xs mb-1">{deploymentBreadcrumbMap.get(exec.deploymentId)}</div>
                )}
                <div className="text-gray-500 text-xs">
                  {exec.createdAt ? `${new Date(exec.createdAt).toLocaleString('en-US', { timeZone: 'UTC' })} UTC` : '-'}
                </div>
                {exec.metrics
                  ? (
                  <div className="mt-2 text-xs space-y-0.5">
                    {exec.metrics.model && (
                      <div><span className="text-gray-400">Model: </span><span className="font-mono">{exec.metrics.model}</span></div>
                    )}
                    {exec.metrics.latency != null && (
                      <div><span className="text-gray-400">Latency: </span><span className="font-mono">{exec.metrics.latency / 1000}s</span></div>
                    )}
                    {exec.metrics.inputTokens != null && (
                      <div><span className="text-gray-400">In: </span><span className="font-mono">{exec.metrics.inputTokens}</span></div>
                    )}
                    {exec.metrics.outputTokens != null && (
                      <div><span className="text-gray-400">Out: </span><span className="font-mono">{exec.metrics.outputTokens}</span></div>
                    )}
                    {exec.metrics.thinkingTokens != null && (
                      <div><span className="text-gray-400">Think: </span><span className="font-mono">{exec.metrics.thinkingTokens}</span></div>
                    )}
                  </div>
                    )
                  : '-'}
                <div className="mt-2 text-xs">
                    <span
                      className={`px-1.5 py-0.5 rounded ${exec.status === 'completed' ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-300'}`}>
                      {exec.status || 'unknown'}
                    </span>
                </div>
              </td>
              <td className="p-2 align-top"><ObjCell data={exec.variablesValues}/></td>
              <td className="p-2 align-top max-w-2xl">
                {/* data-tooltip={JSON.stringify(exec.chatHistory, null, 2)} */}
                <ChatHistoryComponent history={exec.chatHistory}/>
                {/* {renderJsonCell(exec.chatHistory)} */}
              </td>
              <td className="p-2 align-top max-w-2xl overflow-auto">
                {/* data-tooltip={JSON.stringify(exec.result?.output, null, 2)} */}
                <ExecutionOutput output={exec.output}/>
              </td>
              <td className="p-2 align-top"><ObjCell data={exec.metadata}/></td>
            </tr>
          ))}
          {!loading && executions.length === 0 && (
            <tr>
              <td colSpan={6} className="p-8 text-center text-gray-500">No executions found.</td>
            </tr>
          )}
          {hasMore && (
            <tr>
              <td colSpan={6} className="p-4 text-center">
                <button
                  onClick={() => loadExecutions()}
                  disabled={loading}
                  className="btn btn-secondary flex items-center gap-2 mx-auto text-sm disabled:opacity-50"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin"/>}
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </td>
            </tr>
          )}
          </tbody>
        </table>
      </div>

      {/* Execution Detail Modal */}
      {(detailExec || detailLoading) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => {
          setDetailExec(null); setDetailLoading(false)
        }}>
          <div className="relative bg-gray-900 border border-gray-700 rounded-lg p-4 w-[90vw] h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-semibold text-gray-100">
                Execution {detailExec?.id ?? ''}
              </h3>
              <button
                onClick={() => {
                  setDetailExec(null); setDetailLoading(false)
                }}
                className="p-1 bg-gray-700 hover:bg-gray-600 rounded text-gray-300 hover:text-white cursor-pointer"
                title="Close"
              >
                <X size={16}/>
              </button>
            </div>
            {detailLoading
              ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400"/>
              </div>
                )
              : detailExec
                ? (
              <div className="flex-1 overflow-auto custom-scrollbar space-y-4">
                <div>
                  <h4 className="text-xl font-semibold text-gray-200 mb-1">Metrics</h4>
                  <JsonCell data={detailExec.metrics} expand/>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-200 mb-1">Output</h4>
                  <ExecutionOutput output={detailExec.output} expand={true}/>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-200 mb-1">Variables</h4>
                  <ObjCell data={detailExec.variablesValues} expand/>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-200 mb-1">Chat History</h4>
                  <ChatHistoryComponent history={detailExec.chatHistory} expand={true}/>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-200 mb-1">Result (Request / Response)</h4>
                  <JsonCell data={detailExec.result} expand/>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-200 mb-1">Metadata</h4>
                  <ObjCell data={detailExec.metadata} expand/>
                </div>
              </div>
                  )
                : null}
          </div>
        </div>
      )}
    </div>
  )
}
